import {useCallback, useEffect, useRef, useState} from 'react';
import {
    addMoveToCurrentNode,
    a1ToPos,
    calcMatAndMarkup,
    CustomProp,
    Ki,
    TNode,
} from 'ghostban';
import {
    useFetchScenarioSolutionPathContextQuery,
    useLazyFetchScenarioSolutionChildrenQuery,
} from '@api';
import {
    ScenarioSolutionChildNode,
    ScenarioSolutionNodeStats,
    ScenarioSolutionPathContext,
} from '@types';
import {boardSlice} from '@reducers';
import {
    findChildByA1Move,
    getNodeA1Path,
    useDispatch,
    useSelector,
} from '@utils';

type LazyNodeState = {
    childrenCount: number;
    childrenLoaded: boolean;
    isLoading: boolean;
};

type UseLazyScenarioSolutionTreeParams = {
    scenarioId: number | undefined;
    difficulty: string | undefined | null;
    path: string;
    initialToMove: string;
    enabled?: boolean;
};

const STATS_PROP_TOKENS = new Set([
    'NID',
    'RID',
    'SD',
    'SC',
    'ED',
    'TL',
    'UR',
    'SWT',
    'RV',
    'IA',
]);

const PATH_CONTEXT_DEBOUNCE_MS = 250;

const normalizePath = (path: string | null | undefined): string => {
    if (!path || path === '<root>' || path.toLowerCase() === 'root') {
        return '';
    }

    return path
        .split(',')
        .map(move => move.trim())
        .filter(Boolean)
        .join(',');
};

const normalizeToMove = (toMove: string): 'b' | 'w' =>
    toMove === 'w' ? 'w' : 'b';

const getNextToMove = (
    currentNode: TNode | null,
    defaultToMove: 'b' | 'w'
): 'b' | 'w' => {
    if (!currentNode) return defaultToMove;

    const lastMove = currentNode.model.moveProps?.[0];
    if (!lastMove) return defaultToMove;

    if (lastMove.token === 'W') return 'b';
    if (lastMove.token === 'B') return 'w';

    return defaultToMove;
};

const applyStatsToNode = (
    node: TNode,
    stats: ScenarioSolutionNodeStats | null
) => {
    if (!stats) return;

    if (!node.model.customProps) {
        node.model.customProps = [];
    }

    node.model.customProps = node.model.customProps.filter(
        (prop: {token: string}) => !STATS_PROP_TOKENS.has(prop.token)
    );

    const addProp = (token: string, value: unknown) => {
        if (value === null || value === undefined) return;
        node.model.customProps.push(new CustomProp(token, String(value)));
    };

    addProp('NID', stats.nodeId);
    addProp('RID', stats.selectedAnalysisResultId);
    addProp('SD', stats.scoreDelta);
    addProp('SC', stats.score);
    addProp('ED', stats.endness);
    addProp('TL', stats.totalLoss);
    addProp('UR', stats.urgency);
    addProp('SWT', stats.weight);
    addProp('RV', stats.responseValid ? 'true' : 'false');
    addProp('IA', stats.isAnalyzed ? 'true' : 'false');
};

const mergeNodeStats = (
    previous: ScenarioSolutionNodeStats | null | undefined,
    incoming: ScenarioSolutionNodeStats | null
): ScenarioSolutionNodeStats | null => {
    if (!incoming) {
        return previous ?? null;
    }

    // Preserve analyzed values when an older cached context arrives for the same path.
    if (previous?.isAnalyzed && !incoming.isAnalyzed) {
        return {
            ...incoming,
            nodeId: incoming.nodeId ?? previous.nodeId,
            selectedAnalysisResultId: previous.selectedAnalysisResultId,
            score: previous.score,
            scoreDelta: previous.scoreDelta,
            totalLoss: previous.totalLoss,
            urgency: previous.urgency,
            endness: previous.endness,
            weight: previous.weight,
            isAnalyzed: true,
            tags: previous.tags,
        };
    }

    return incoming;
};

const mergeStatsByPath = (
    previous: Record<string, ScenarioSolutionNodeStats | null>,
    incoming: Record<string, ScenarioSolutionNodeStats | null>
): Record<string, ScenarioSolutionNodeStats | null> => {
    const merged = {...previous};

    for (const [path, stats] of Object.entries(incoming)) {
        merged[path] = mergeNodeStats(previous[path], stats);
    }

    return merged;
};

const sortChildrenByWeight = (
    node: TNode,
    statsByPath: Record<string, ScenarioSolutionNodeStats | null>
) => {
    if (node.children.length < 2) return;

    const getChildPath = (child: TNode) => normalizePath(getNodeA1Path(child));
    const getChildWeight = (child: TNode) =>
        statsByPath[getChildPath(child)]?.weight ?? Number.NEGATIVE_INFINITY;

    node.children.sort((a: TNode, b: TNode) => {
        const weightDiff = getChildWeight(b) - getChildWeight(a);
        if (weightDiff !== 0) return weightDiff;

        return getChildPath(a).localeCompare(getChildPath(b));
    });
};

const mergeLazyNodeStates = (
    previous: Record<string, LazyNodeState>,
    incoming: Record<string, LazyNodeState>
): Record<string, LazyNodeState> => {
    const merged = {...previous};

    for (const [path, state] of Object.entries(incoming)) {
        merged[path] = {
            ...state,
            childrenLoaded:
                state.childrenLoaded || previous[path]?.childrenLoaded || false,
        };
    }

    return merged;
};

export const useLazyScenarioSolutionTree = ({
    scenarioId,
    difficulty,
    path,
    initialToMove,
    enabled = true,
}: UseLazyScenarioSolutionTreeParams) => {
    const {root} = useSelector(s => s.board);
    const dispatch = useDispatch();
    const [nodeStates, setNodeStates] = useState<Record<string, LazyNodeState>>(
        {}
    );
    const [statsByPath, setStatsByPath] = useState<
        Record<string, ScenarioSolutionNodeStats | null>
    >({});
    const statsByPathRef = useRef<
        Record<string, ScenarioSolutionNodeStats | null>
    >({});
    const loadingPaths = useRef<Set<string>>(new Set());
    const previousTreeKey = useRef<string>('');
    const normalizedPath = normalizePath(path);
    const [debouncedNormalizedPath, setDebouncedNormalizedPath] =
        useState(normalizedPath);
    const normalizedInitialToMove = normalizeToMove(initialToMove);

    useEffect(() => {
        const timeoutId = window.setTimeout(() => {
            setDebouncedNormalizedPath(normalizedPath);
        }, PATH_CONTEXT_DEBOUNCE_MS);

        return () => {
            window.clearTimeout(timeoutId);
        };
    }, [normalizedPath]);

    useEffect(() => {
        const treeKey = `${scenarioId ?? ''}:${difficulty ?? ''}`;
        if (previousTreeKey.current === treeKey) return;

        previousTreeKey.current = treeKey;
        loadingPaths.current.clear();
        statsByPathRef.current = {};
        setNodeStates({});
        setStatsByPath({});
    }, [difficulty, scenarioId]);

    useEffect(() => {
        statsByPathRef.current = statsByPath;
    }, [statsByPath]);

    const {
        data: pathContext,
        isFetching: isFetchingPathContext,
        refetch: refetchPathContext,
    } = useFetchScenarioSolutionPathContextQuery(
        {
            scenarioId: scenarioId ?? 0,
            difficulty: difficulty ?? '',
            path: debouncedNormalizedPath,
        },
        {
            skip: !enabled || !scenarioId || !difficulty,
            refetchOnMountOrArgChange: false,
        }
    );
    const [fetchChildren] = useLazyFetchScenarioSolutionChildrenQuery();

    const ensurePath = useCallback(
        (targetPath: string): TNode | null => {
            if (!root) return null;

            const normalizedTargetPath = normalizePath(targetPath);
            if (!normalizedTargetPath) {
                return root;
            }

            const moves = normalizedTargetPath.split(',');
            let node = root;
            let nextToMove = getNextToMove(node, normalizedInitialToMove);

            for (const move of moves) {
                const existingNode = findChildByA1Move(node, move);
                if (existingNode) {
                    node = existingNode;
                    nextToMove = nextToMove === 'w' ? 'b' : 'w';
                    continue;
                }

                const pos = a1ToPos(move);
                const {mat} = calcMatAndMarkup(node);
                const turn = nextToMove === 'w' ? Ki.White : Ki.Black;
                const newNode = addMoveToCurrentNode(
                    node,
                    mat,
                    pos.x,
                    pos.y,
                    turn
                );

                if (!newNode) {
                    return null;
                }

                node = newNode;
                nextToMove = nextToMove === 'w' ? 'b' : 'w';
            }

            return node;
        },
        [normalizedInitialToMove, root]
    );

    const mergeChildren = useCallback(
        (parentPath: string, children: ScenarioSolutionChildNode[]) => {
            const parentNode = ensurePath(parentPath);
            if (!parentNode) return;

            const nextStates: Record<string, LazyNodeState> = {};
            const nextStats: Record<string, ScenarioSolutionNodeStats | null> =
                {};

            for (const child of children) {
                const childNode = ensurePath(child.path);
                if (!childNode) continue;

                const normalizedChildPath = normalizePath(child.path);
                const childStats = mergeNodeStats(
                    statsByPathRef.current[normalizedChildPath],
                    child.stats
                );
                applyStatsToNode(childNode, childStats);
                nextStats[normalizedChildPath] = childStats;
                nextStates[normalizedChildPath] = {
                    childrenCount: child.childrenCount,
                    childrenLoaded: false,
                    isLoading: loadingPaths.current.has(normalizedChildPath),
                };
            }

            const normalizedParentPath = normalizePath(parentPath);
            nextStates[normalizedParentPath] = {
                childrenCount: children.length,
                childrenLoaded: true,
                isLoading: false,
            };
            sortChildrenByWeight(parentNode, {
                ...statsByPathRef.current,
                ...nextStats,
            });

            setNodeStates(prev => mergeLazyNodeStates(prev, nextStates));
            setStatsByPath(prev => mergeStatsByPath(prev, nextStats));
            dispatch(boardSlice.actions.forceUpdate());
        },
        [dispatch, ensurePath]
    );

    const mergePathContext = useCallback(
        (context: ScenarioSolutionPathContext) => {
            const nextStates: Record<string, LazyNodeState> = {};
            const nextStats: Record<string, ScenarioSolutionNodeStats | null> =
                {};
            const parentsToSort = new Set<TNode>();

            for (const contextNode of context.nodes) {
                const node = ensurePath(contextNode.path);
                const normalizedContextPath = normalizePath(contextNode.path);
                if (node) {
                    const contextStats = mergeNodeStats(
                        statsByPathRef.current[normalizedContextPath],
                        contextNode.stats
                    );
                    applyStatsToNode(node, contextStats);
                    nextStats[normalizedContextPath] = contextStats;
                    parentsToSort.add(node);
                }

                nextStates[normalizedContextPath] = {
                    childrenCount: contextNode.childrenCount,
                    childrenLoaded: true,
                    isLoading: loadingPaths.current.has(normalizedContextPath),
                };

                for (const child of contextNode.children) {
                    const childNode = ensurePath(child.path);
                    if (!childNode) continue;

                    const normalizedChildPath = normalizePath(child.path);
                    const childStats = mergeNodeStats(
                        statsByPathRef.current[normalizedChildPath],
                        child.stats
                    );
                    applyStatsToNode(childNode, childStats);
                    nextStats[normalizedChildPath] = childStats;
                    nextStates[normalizedChildPath] = {
                        childrenCount: child.childrenCount,
                        childrenLoaded: false,
                        isLoading:
                            loadingPaths.current.has(normalizedChildPath),
                    };
                }
            }

            const sortableStats = {
                ...statsByPathRef.current,
                ...nextStats,
            };
            parentsToSort.forEach(parentNode =>
                sortChildrenByWeight(parentNode, sortableStats)
            );
            setNodeStates(prev => mergeLazyNodeStates(prev, nextStates));
            setStatsByPath(prev => mergeStatsByPath(prev, nextStats));
            dispatch(boardSlice.actions.forceUpdate());
        },
        [dispatch, ensurePath]
    );

    useEffect(() => {
        if (!root || Object.keys(statsByPath).length === 0) return;

        const parentsToSort = new Set<TNode>();
        for (const [path, stats] of Object.entries(statsByPath)) {
            if (!stats) continue;

            const node = ensurePath(path);
            if (node) {
                applyStatsToNode(node, stats);
                if (node.parent) {
                    parentsToSort.add(node.parent);
                }
            }
        }

        parentsToSort.forEach(parentNode =>
            sortChildrenByWeight(parentNode, statsByPath)
        );
        dispatch(boardSlice.actions.forceUpdate());
    }, [dispatch, ensurePath, root, statsByPath]);

    useEffect(() => {
        if (!root || !pathContext) return;

        mergePathContext(pathContext);
    }, [mergePathContext, pathContext, root]);

    const loadChildrenForNode = useCallback(
        async (nodeOrPath: TNode | string) => {
            if (!scenarioId || !difficulty) return;

            const targetPath =
                typeof nodeOrPath === 'string'
                    ? normalizePath(nodeOrPath)
                    : normalizePath(getNodeA1Path(nodeOrPath));

            if (nodeStates[targetPath]?.childrenLoaded) {
                return;
            }

            if (loadingPaths.current.has(targetPath)) {
                return;
            }

            loadingPaths.current.add(targetPath);
            setNodeStates(prev => ({
                ...prev,
                [targetPath]: {
                    childrenCount: prev[targetPath]?.childrenCount ?? 0,
                    childrenLoaded: prev[targetPath]?.childrenLoaded ?? false,
                    isLoading: true,
                },
            }));

            let loaded = false;
            try {
                const result = await fetchChildren({
                    scenarioId,
                    difficulty,
                    path: targetPath,
                }).unwrap();
                mergeChildren(result.path, result.children);
                loaded = true;
            } catch (error) {
                console.error(
                    'Failed to load scenario solution children',
                    error
                );
            } finally {
                loadingPaths.current.delete(targetPath);
                setNodeStates(prev => ({
                    ...prev,
                    [targetPath]: {
                        childrenCount: prev[targetPath]?.childrenCount ?? 0,
                        childrenLoaded:
                            loaded || prev[targetPath]?.childrenLoaded || false,
                        isLoading: false,
                    },
                }));
            }
        },
        [difficulty, fetchChildren, mergeChildren, nodeStates, scenarioId]
    );

    const getStatsForPath = useCallback(
        (targetPath: string) => statsByPath[normalizePath(targetPath)] ?? null,
        [statsByPath]
    );

    return {
        isFetchingPathContext,
        loadChildrenForNode,
        nodeStates,
        getStatsForPath,
        refetchPathContext,
    };
};
